import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useAuthStore } from "@/stores/authStore";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Spinner } from "@/components/ui/Spinner";
import { ROUTES } from "@/lib/constants";

// Lazy-loaded pages
const Landing = lazy(() => import("@/pages/Landing").then(m => ({ default: m.Landing })));
const Login = lazy(() => import("@/pages/Login").then(m => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Register").then(m => ({ default: m.Register })));
const Dashboard = lazy(() => import("@/pages/Dashboard").then(m => ({ default: m.Dashboard })));
const Projects = lazy(() => import("@/pages/Projects").then(m => ({ default: m.Projects })));
const CreateProject = lazy(() => import("@/pages/CreateProject").then(m => ({ default: m.CreateProject })));
const Editor = lazy(() => import("@/pages/Editor").then(m => ({ default: m.Editor })));
const Guests = lazy(() => import("@/pages/Guests").then(m => ({ default: m.Guests })));
const Files = lazy(() => import("@/pages/Files").then(m => ({ default: m.Files })));
const MusicLibrary = lazy(() => import("@/pages/MusicLibrary").then(m => ({ default: m.MusicLibrary })));
const Settings = lazy(() => import("@/pages/Settings").then(m => ({ default: m.Settings })));
const Profile = lazy(() => import("@/pages/Profile").then(m => ({ default: m.Profile })));
const Billing = lazy(() => import("@/pages/Billing").then(m => ({ default: m.Billing })));
const Domains = lazy(() => import("@/pages/Domains").then(m => ({ default: m.Domains })));
const Support = lazy(() => import("@/pages/Support").then(m => ({ default: m.Support })));
const Templates = lazy(() => import("@/pages/Templates").then(m => ({ default: m.Templates })));
const Pricing = lazy(() => import("@/pages/Pricing").then(m => ({ default: m.Pricing })));
const NotFound = lazy(() => import("@/pages/NotFound").then(m => ({ default: m.NotFound })));
const Privacy = lazy(() => import("@/pages/Privacy").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Terms").then(m => ({ default: m.Terms })));
const Offer = lazy(() => import("@/pages/Offer").then(m => ({ default: m.Offer })));
const WeddingSiteView = lazy(() => import("@/pages/WeddingSiteView").then(m => ({ default: m.WeddingSiteView })));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers").then(m => ({ default: m.AdminUsers })));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments").then(m => ({ default: m.AdminPayments })));

function PageLoader() {
  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center">
      <Spinner size="lg" className="text-gold" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Init auth from localStorage
    try {
      const raw = localStorage.getItem("weddinv_current_user");
      if (raw) {
        const user = JSON.parse(raw);
        setUser(user);
      }
    } catch {
      // ignore
    }
    setLoading(false);
  }, [setUser, setLoading]);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#FAF8F5",
            color: "#2C1810",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 8px 40px rgba(44, 24, 16, 0.08)",
          },
          success: {
            iconTheme: {
              primary: "#7D9B76",
              secondary: "#FAF8F5",
            },
          },
          error: {
            iconTheme: {
              primary: "#C17767",
              secondary: "#FAF8F5",
            },
          },
        }}
      />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/lending" replace />} />
          <Route path={ROUTES.HOME} element={<Landing />} />
          <Route path={ROUTES.TEMPLATES} element={<Templates />} />
          <Route path={ROUTES.PRICING} element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/offer" element={<Offer />} />

          {/* Auth Routes */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              <GuestRoute>
                <Register />
              </GuestRoute>
            }
          />

          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="projects" element={<Projects />} />
            <Route path="create" element={<CreateProject />} />
            <Route path="editor/:id" element={<Editor />} />
            <Route path="guests" element={<Guests />} />
            <Route path="files" element={<Files />} />
            <Route path="music" element={<MusicLibrary />} />
            <Route path="domains" element={<Domains />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="billing" element={<Billing />} />
            <Route path="support" element={<Support />} />
          </Route>

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="payments" element={<AdminPayments />} />
          </Route>

          {/* Published Wedding Site - must be after all specific routes */}
          <Route path="/:slug" element={<WeddingSiteView />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
